import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import { useSampleData } from '../contexts/SampleDataContext';
import { buildSampleCreditCards } from '../data/sampleCreditCards';
import { getAnalyticsCards } from '../services/analyticsService';
import {
  getDashboardRefreshGeneration,
  subscribeDashboardRefresh,
} from '../services/dashboardRefreshBus';
import type { AnalyticsPeriod, CardSpendResponse, CardSpendRow } from '../types/analytics';
import type { SpendCategoryNode } from '../types/financial';

export type CardSpendData = {
  isLive: boolean;
  loading: boolean;
  error: string | null;
  cards: CardSpendRow[];
  totals: CardSpendResponse['totals'];
  refresh: () => void;
};

export function useCardSpend(options: {
  period: AnalyticsPeriod;
  periodKey: string;
  sampleCategories: SpendCategoryNode[];
}): CardSpendData {
  const { isSampleMode } = useSampleData();
  const { isAuthenticated, tokens } = useAuth();
  const { view: householdView, viewReady } = useHouseholdMembership();
  const useLive = isAuthenticated && !isSampleMode && !!tokens?.accessToken;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<CardSpendResponse | null>(null);
  const [tick, setTick] = useState(0);
  const seenGenerationRef = useRef(0);
  const hasLiveRef = useRef(false);
  hasLiveRef.current = live != null;

  useEffect(() => {
    const applyGeneration = (generation: number) => {
      if (generation === seenGenerationRef.current) return;
      seenGenerationRef.current = generation;
      setTick((v) => v + 1);
    };
    applyGeneration(getDashboardRefreshGeneration());
    return subscribeDashboardRefresh(applyGeneration);
  }, []);

  useEffect(() => {
    if (!useLive || !tokens?.accessToken || !viewReady) {
      if (!useLive) {
        setLive(null);
        setError(null);
      }
      return;
    }

    let cancelled = false;
    setError(null);
    if (!hasLiveRef.current) setLoading(true);

    void getAnalyticsCards({
      accessToken: tokens.accessToken,
      period: options.period,
      periodKey: options.periodKey,
      view: householdView,
      forceRefresh: true,
    })
      .then((response) => {
        if (cancelled) return;
        setLive(response);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        if (!hasLiveRef.current) setLive(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    useLive,
    tokens?.accessToken,
    options.period,
    options.periodKey,
    householdView,
    viewReady,
    tick,
  ]);

  return useMemo(() => {
    const refresh = () => setTick((v) => v + 1);

    if (!useLive) {
      const cards = buildSampleCreditCards(options.periodKey, options.sampleCategories);
      const totals = cards.reduce(
        (acc, card) => ({
          totalSpend: acc.totalSpend + card.totalSpend,
          totalStatementBalance: acc.totalStatementBalance + (card.statementBalance ?? 0),
          totalMinimumPayment: acc.totalMinimumPayment + (card.minimumPayment ?? 0),
        }),
        { totalSpend: 0, totalStatementBalance: 0, totalMinimumPayment: 0 },
      );
      return {
        isLive: false,
        loading: false,
        error: null,
        cards,
        totals,
        refresh,
      };
    }

    return {
      isLive: true,
      loading,
      error,
      cards: live?.cards ?? [],
      totals: live?.totals ?? {
        totalSpend: 0,
        totalStatementBalance: 0,
        totalMinimumPayment: 0,
      },
      refresh,
    };
  }, [
    useLive,
    options.periodKey,
    options.sampleCategories,
    loading,
    error,
    live,
  ]);
}
